<template>
  <form id="registration" method="post" @submit.prevent="validateAndSubmit">
    <div class="col">
      <customer-details-form
        :pay-in-terms="payInTerms"
        :customer-details="order.customerDetails"
        :store-user="false"
        @property-changed="data => customerDetailsPropertyChanged(data)"
      />
    </div>
    <div class="col">
      <customer-address-form
        :address="order.invoiceAddress"
        :store-user="false"
        @AddressPropertyChanged="e => $emit('AddressPropertyChanged', e)"
      />
    </div>
    <fieldset class="submit">
      <div class="form-row">
        <div class="form-column">
          <input
            v-if="user || isRegistered || !payInTerms"
            id="updateAccount"
            :value="user || isRegistered ? $t('registrationForm.next') : $t('registrationForm.nextWithoutAccount')"
            type="submit"
            name="updateAccount"
            class="link-button">
        </div>
      </div>
    </fieldset>
    <customer-create-account-form
      v-if="showCreateAccountForm"
      @email-address-changed="useCustomerDetailsEmailAddressForRegistration = false"
      @submit="validateAndRegisterAndSubmit"/>

    <v-modal
      v-if="showApprovePaymentInTermsModal"
      :modal-id="'flashMessage'"
      :modal-class="'confirm-modal'"
      :title-id="'flashMessageTitle'"
      @close="showApprovePaymentInTermsModal = false">
      <h2 id="flashMessageTitleApproveTerms">{{ $t('registrationForm.approvePaymentInTermsModalTitle') }}</h2>
      <p class="error" v-html="$t('registrationForm.approvePaymentInTermsModalContent')"/>
    </v-modal>
    <v-modal
      v-if="showChoosePaymentTypeModal"
      :modal-id="'flashMessagePaymentType'"
      :modal-class="'confirm-modal'"
      :title-id="'flashMessageTitlePaymentType'"
      @close="showChoosePaymentTypeModal = false">
      <h2 id="flashMessageTitleChoosePayment">{{ $t('registrationForm.choosePaymentTypeModalTitle') }}</h2>
      <p class="error" v-html="$t('registrationForm.choosePaymentTypeModalContent')"/>
    </v-modal>
    <v-modal
      v-if="showGenericErrorModal"
      :modal-id="'flashMessage'"
      :modal-class="'confirm-modal'"
      :title-id="'flashMessageTitle'"
      @close="showGenericErrorModal = false">
      <h2 id="flashMessageTitle">{{ $t('registrationForm.genericErrorModalTitle') }}</h2>
      <p class="error" v-html="$t('registrationForm.genericErrorModalContent')"/>
    </v-modal>
  </form>
</template>

<script>
/* @flow */
import { mapState, mapGetters } from 'vuex'
import CustomerDetailsForm from './CustomerDetailsForm'
import CustomerAddressForm from './CustomerAddressForm'
import CustomerCreateAccountForm from './CustomerCreateAccountForm'
import VModal from '../components/modals/Modal'
import {
  RESET,
  SET_EMAIL_ADDRESS,
  SET_REGISTRATION_VALIDATION_ERRORS,
  SET_REGISTRATION_VALIDATION_SUCCESS
} from '../store/modules/registration/mutation-types'
import { SET_CUSTOMER_NUMBER, CUSTOMER_DETAILS_CHANGED } from '../store/modules/cart/mutation-types'
import { REGISTRATION_STATUS_ERROR, REGISTRATION_STATUS_SUCCESS } from '../store/modules/registration'
import { ORDER_VALIDATION_STAGE_CUSTOMER_DETAILS } from '../order-api/order-api-client-constants'
import FocusMixin from '../mixins/focus'

export default {
  name: 'RegistrationForm',
  components: {
    CustomerDetailsForm,
    CustomerAddressForm,
    CustomerCreateAccountForm,
    VModal
  },
  mixins: [FocusMixin],
  props: {
    payInTerms: {
      type: Boolean,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: false,
      default: null
    },
    mustPaymentTypeBeSelected: {
      type: Boolean,
      required: true
    },
    isPaymentTypeSelected: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      showApprovePaymentInTermsModal: false,
      showGenericErrorModal: false,
      showChoosePaymentTypeModal: false,
      useCustomerDetailsEmailAddressForRegistration: true,
      isValidating: false,
      registrationCredentials: { emailAddress: '', password: '', passwordConfirm: '' }
    }
  },
  computed: {
    ...mapState({
      hasUnknownRegistrationError:
        state => state.registration.status === REGISTRATION_STATUS_ERROR && state.registration.registrationValidationErrors.length === 0,
      isRegistered: state => state.registration.status === REGISTRATION_STATUS_SUCCESS,
      hasValidationErrors: state => (
        (state.cart.orderValidationErrors || []).length + (state.user.userValidationErrors || []).length) > 0
    }),
    ...mapGetters({
      customerDetailsChanged: 'cart/customerDetailsChanged'
    }),
    showCreateAccountForm: function () {
      return !this.user && !this.isRegistered
    }
  },
  watch: {
    isValidating: function (isValidating, wasValidating) {
      if (wasValidating && !isValidating && this.hasValidationErrors) {
        this.startScrollIntoView()
      }
    }
  },
  methods:
    {
      validate: async function ({validateCredentials} = {validateCredentials: false}) {
        if (this.mustPaymentTypeBeSelected && !this.assertPaymentTypeSelected()) {
          return false
        }
        if (!this.assertApprovePaymentInTerms()) {
          return false
        }
        this.isValidating = true
        try {
          await Promise.all([
            this.validateOrder(),
            (validateCredentials ? this.validateCredentials() : this.resetCredentialsValidationErrors())
          ])
        } catch (error) {
          this.isValidating = false
          this.showGenericErrorModal = true

          return false
        }
        this.isValidating = false

        return this.$store.state.cart.orderValidationErrors.length === 0 &&
          this.$store.state.registration.registrationValidationErrors.length === 0
      },
      validateOrder: async function () {
        return this.$store.dispatch('cart/validateOrder', {
          order: this.order,
          stage: ORDER_VALIDATION_STAGE_CUSTOMER_DETAILS
        })
      },
      validateCredentials: async function () {
        return this.$store.dispatch('registration/validateRegistration', {
          customerDetails: this.order.customerDetails,
          address: this.order.invoiceAddress,
          credentials: this.registrationCredentials
        })
      },
      resetCredentialsValidationErrors: async function () {
        return this.$store.dispatch('registration/resetValidationErrors')
      },

      updateLoggedInUser: async function () {
        if (this.user) {
          try {
            await this.$store.dispatch('user/updateUser')
          } catch (error) {
            return false
          }
        }

        return true
      },
      submit: async function () {
        if (!await this.updateLoggedInUser()) {
          return false
        }
        if (this.customerDetailsChanged) {
          await this.$store.dispatch('cart/clearOrderCreated')
        }
        this.$store.dispatch('cart/resetCustomerDetailsChanged')
        this.$router.push({ name: 'PaymentSelection', params: { lang: this.$route.params.lang } }).catch(() => {})
      },
      validateAndSubmit: async function () {
        if (!await this.validate()) {
          return false
        }
        await this.submit()
      },
      validateAndRegisterAndSubmit: async function (credentials) {
        this.registrationCredentials = credentials
        if (!await this.validate({validateCredentials: true})) {
          return false
        }
        try {
          this.$store.commit('registration/' + RESET)
          const result = await this.$container.userApiClient.register(this.order.customerDetails, this.order.invoiceAddress, credentials)
          if (result.errors && result.errors.length > 0) {
            this.$store.commit('registration/' + SET_REGISTRATION_VALIDATION_ERRORS, result.errors)

            return
          }
          this.$store.commit('cart/' + SET_CUSTOMER_NUMBER, result.customerNumber)
        } catch (e) {
          if (e.hasOwnProperty('errors')) { // can not use instanceof ValidationError https://github.com/babel/babel/issues/4480
            this.$store.commit('registration/' + SET_REGISTRATION_VALIDATION_ERRORS, e.errors)
          } else {
            this.$store.commit('registration/' + SET_REGISTRATION_VALIDATION_ERRORS, [])
            this.showGenericErrorModal = true
          }

          return
        }
        this.$store.commit('registration/' + SET_REGISTRATION_VALIDATION_SUCCESS)
        await this.submit()
      },
      assertApprovePaymentInTerms: function () {
        if (this.payInTerms && !this.order.approvePaymentInTerms) {
          this.showApprovePaymentInTermsModal = true
          return false
        }
        return true
      },
      assertPaymentTypeSelected: function () {
        if (!this.isPaymentTypeSelected) {
          this.showChoosePaymentTypeModal = true
          return false
        }
        return true
      },
      customerDetailsPropertyChanged: function ({ name, value }) {
        if (name === 'emailAddress' && this.useCustomerDetailsEmailAddressForRegistration) {
          this.$store.commit('registration/' + SET_EMAIL_ADDRESS, value)
        }
        this.$store.commit('cart/' + CUSTOMER_DETAILS_CHANGED, true)
      }
    }
}
</script>



// WEBPACK FOOTER //
// src/forms/RegistrationForm.vue